/**
 * PrimaryNav — sidebar navigation rail.
 *
 * Renders 2 live items (Chat + Dashboard) per §11 Q5 of the approved Phase 2
 * plan, plus a 3rd "Portfolio" item that is opt-in via `isInternalStaff`
 * (Portfolio Health Report — internal-staff-only). Extensible for future
 * items without rewriting the component. All other hidden future slots
 * ("Team activity", "Files", "Tasks") remain omitted entirely.
 *
 * Class hooks (preservation contract — §4.1):
 *   .sidebar-primary-nav
 *   .sidebar-primary-nav-item
 *   .sidebar-primary-nav-item--active
 *
 * ARIA contract (§4.2 S3):
 *   <nav aria-label="Primary">
 *   <ul role="list">
 *   active item: aria-current="page"
 */

import React from "react";

export interface PrimaryNavProps {
	view: "chat" | "dashboard" | "portfolio";
	onSetView: (view: "chat" | "dashboard" | "portfolio") => void;
	/** Phase 2: always 0 (stub for future unread badge). */
	unreadCount?: number;
	/** Phase 2: mapped from kbStats if available, else undefined. */
	docCount?: number;
	/** Gates the "Portfolio" nav item (Portfolio Health Report). Defaults to falsy — hidden. */
	isInternalStaff?: boolean;
}

interface NavItem {
	id: "chat" | "dashboard" | "portfolio";
	label: string;
	icon: React.ReactNode;
}

/**
 * Chat icon — transcribed from the Claude Design handoff (icons.jsx, key "chat").
 * 20×20 viewBox, strokeWidth 1.6, rounded caps/joins — matches the design system
 * icon set exactly. All future nav icons should follow the same pattern.
 */
function ChatIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M4 5h12v8H9l-4 3v-3H4z" />
		</svg>
	);
}

/**
 * Dashboard icon — transcribed from the Claude Design handoff (icons.jsx, key "dashboard").
 * Four rounded squares in a 2×2 grid. Same 20×20 / 1.6-stroke system as ChatIcon.
 */
function DashboardIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<rect x="3" y="3" width="6" height="6" rx="1" />
			<rect x="11" y="3" width="6" height="6" rx="1" />
			<rect x="3" y="11" width="6" height="6" rx="1" />
			<rect x="11" y="11" width="6" height="6" rx="1" />
		</svg>
	);
}

/**
 * Portfolio icon — a simple upward trend/bar-chart glyph, following the same
 * 20×20 viewBox / 1.6-stroke system as ChatIcon and DashboardIcon.
 */
function PortfolioIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M3 16.5h14" />
			<path d="M5.5 16.5v-4" />
			<path d="M9.5 16.5v-7" />
			<path d="M13.5 16.5v-3.5" />
			<path d="M17 16.5V6" />
			<path d="M13 4l4-1.5 1.5 1.5" />
		</svg>
	);
}

const BASE_NAV_ITEMS: NavItem[] = [
	{ id: "chat", label: "Chat", icon: <ChatIcon /> },
	{ id: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
];

const PORTFOLIO_NAV_ITEM: NavItem = { id: "portfolio", label: "Portfolio", icon: <PortfolioIcon /> };

export default function PrimaryNav({
	view,
	onSetView,
	unreadCount = 0,
	docCount,
	isInternalStaff,
}: PrimaryNavProps) {
	// Portfolio is opt-in and always appended after Chat/Dashboard — it must
	// never render before the Chat item (see PrimaryNav.portfolio.test.tsx D11).
	const navItems: NavItem[] = isInternalStaff === true
		? [...BASE_NAV_ITEMS, PORTFOLIO_NAV_ITEM]
		: BASE_NAV_ITEMS;

	return (
		<nav
			className="sidebar-primary-nav"
			aria-label="Primary"
		>
			<ul role="list">
				{navItems.map((item) => {
					const isActive = view === item.id;
					return (
						<li key={item.id}>
							<button
								type="button"
								className={`sidebar-primary-nav-item${isActive ? " sidebar-primary-nav-item--active" : ""}`}
								onClick={() => onSetView(item.id)}
								aria-current={isActive ? "page" : undefined}
							>
								<span className="sidebar-primary-nav-item-icon" aria-hidden="true">
									{item.icon}
								</span>
								<span className="sidebar-primary-nav-item-label">{item.label}</span>
								{item.id === "chat" && unreadCount > 0 && (
									<span
										className="sidebar-primary-nav-item-badge"
										aria-label={`${unreadCount} unread`}
									>
										{unreadCount}
									</span>
								)}
								{item.id === "dashboard" && docCount !== undefined && (
									<span className="sidebar-primary-nav-item-doc-count" aria-hidden="true">
										{docCount}
									</span>
								)}
							</button>
						</li>
					);
				})}
			</ul>
		</nav>
	);
}
